import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateScript } from '../../store/actions/scriptsActions';
import { CrossIcon } from '../../assets/svgs/svgs';

const EditScriptModal = ({ showModal, onClose, script }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState({
    Title: '',
    Introduction: '',
    IntroRollIn: '',
    WhatIsCoreConcept: '',
    KeyElements: '',
    SettingsTools: [],
    ExtraTip: '',
    FocusingStabilizing: [],
    CompositionEnhancements: [],
    CommonMistakes: [],
    EditingRefinement: '',
    Conclusion: '',
    CallToAction: '',
    ImportantPoints: {
      visualCuesAndSceneIdeas: [],
      stepByStepDemonstrations: [],
      storytellingAndRelatableExamples: [],
      engagementAndPacingEnhancers: [],
      additionalNotes: []
    }
  });

  const parseScriptContent = (content) => {
    if (!content) return {};

    const result = {
      Title: '',
      Introduction: '',
      IntroRollIn: '',
      WhatIsCoreConcept: '',
      KeyElements: '',
      SettingsTools: [],
      ExtraTip: '',
      FocusingStabilizing: [],
      CompositionEnhancements: [],
      CommonMistakes: [],
      EditingRefinement: '',
      Conclusion: '',
      CallToAction: '',
      ImportantPoints: {
        visualCuesAndSceneIdeas: [],
        stepByStepDemonstrations: [],
        storytellingAndRelatableExamples: [],
        engagementAndPacingEnhancers: [],
        additionalNotes: []
      }
    };

    let parsed = null;
    
    if (typeof content === 'string') {
      try {
        parsed = JSON.parse(content);
      } catch (e) {
        return result;
      }
    } else if (typeof content === 'object') {
      parsed = content;
    }

    if (!parsed) return result;

    const scriptData = parsed.script || parsed;
    const importantPointsData = parsed.importantPoints || scriptData.importantPoints || {};

    result.Title = scriptData.title || '';
    result.Introduction = scriptData.introduction || '';
    result.IntroRollIn = scriptData.introRollIn || '';
    result.WhatIsCoreConcept = scriptData.whatIsCoreConcept || '';
    result.KeyElements = scriptData.keyElements || '';
    result.SettingsTools = Array.isArray(scriptData.settingsTools) ? scriptData.settingsTools : [];
    result.ExtraTip = scriptData.extraTip || '';
    result.FocusingStabilizing = Array.isArray(scriptData.focusingStabilizing) ? scriptData.focusingStabilizing : [];
    result.CompositionEnhancements = Array.isArray(scriptData.compositionEnhancements) ? scriptData.compositionEnhancements : [];
    result.CommonMistakes = Array.isArray(scriptData.commonMistakes) ? scriptData.commonMistakes : [];
    result.EditingRefinement = scriptData.editingRefinement || '';
    result.Conclusion = scriptData.conclusion || '';
    result.CallToAction = scriptData.callToAction || '';

    result.ImportantPoints = {
      visualCuesAndSceneIdeas: Array.isArray(importantPointsData.visualCuesAndSceneIdeas) ? importantPointsData.visualCuesAndSceneIdeas : [],
      stepByStepDemonstrations: Array.isArray(importantPointsData.stepByStepDemonstrations) ? importantPointsData.stepByStepDemonstrations : [],
      storytellingAndRelatableExamples: Array.isArray(importantPointsData.storytellingAndRelatableExamples) ? importantPointsData.storytellingAndRelatableExamples : [],
      engagementAndPacingEnhancers: Array.isArray(importantPointsData.engagementAndPacingEnhancers) ? importantPointsData.engagementAndPacingEnhancers : [],
      additionalNotes: Array.isArray(importantPointsData.additionalNotes) ? importantPointsData.additionalNotes : []
    };

    return result;
  };

  useEffect(() => {
    if (script && showModal) {
      const scriptContent = script.content || '';
      const parsed = parseScriptContent(scriptContent);

      setEditData({
        Title: parsed.Title || script.Title || '',
        Introduction: parsed.Introduction || script.Introduction || '',
        IntroRollIn: parsed.IntroRollIn || '',
        WhatIsCoreConcept: parsed.WhatIsCoreConcept || '',
        KeyElements: parsed.KeyElements || '',
        SettingsTools: parsed.SettingsTools || [],
        ExtraTip: parsed.ExtraTip || '',
        FocusingStabilizing: parsed.FocusingStabilizing || [],
        CompositionEnhancements: parsed.CompositionEnhancements || [],
        CommonMistakes: parsed.CommonMistakes || [],
        EditingRefinement: parsed.EditingRefinement || '',
        Conclusion: parsed.Conclusion || script.Conclusion || '',
        CallToAction: parsed.CallToAction || script.CallToAction || '',
        ImportantPoints: parsed.ImportantPoints || {
          visualCuesAndSceneIdeas: [],
          stepByStepDemonstrations: [],
          storytellingAndRelatableExamples: [],
          engagementAndPacingEnhancers: [],
          additionalNotes: []
        }
      });
    }
  }, [script, showModal]);

  const convertToJSON = () => {
    const output = {
      script: {
        title: editData.Title,
        introduction: editData.Introduction,
        introRollIn: editData.IntroRollIn,
        whatIsCoreConcept: editData.WhatIsCoreConcept,
        keyElements: editData.KeyElements,
        settingsTools: editData.SettingsTools.filter(s => s.trim()),
        extraTip: editData.ExtraTip,
        focusingStabilizing: editData.FocusingStabilizing.filter(s => s.trim()),
        compositionEnhancements: editData.CompositionEnhancements.filter(s => s.trim()),
        commonMistakes: editData.CommonMistakes.filter(s => s.trim()),
        editingRefinement: editData.EditingRefinement,
        conclusion: editData.Conclusion,
        callToAction: editData.CallToAction
      },
      importantPoints: {
        visualCuesAndSceneIdeas: editData.ImportantPoints.visualCuesAndSceneIdeas.filter(s => s.trim()),
        stepByStepDemonstrations: editData.ImportantPoints.stepByStepDemonstrations.filter(s => s.trim()),
        storytellingAndRelatableExamples: editData.ImportantPoints.storytellingAndRelatableExamples.filter(s => s.trim()),
        engagementAndPacingEnhancers: editData.ImportantPoints.engagementAndPacingEnhancers.filter(s => s.trim()),
        additionalNotes: editData.ImportantPoints.additionalNotes.filter(s => s.trim())
      }
    };
    return JSON.stringify(output);
  };

  const handleAddArrayItem = (fieldName) => {
    setEditData(prev => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), '']
    }));
  };

  const handleRemoveArrayItem = (fieldName, idx) => {
    setEditData(prev => {
      const updated = [...(prev[fieldName] || [])];
      updated.splice(idx, 1);
      return { ...prev, [fieldName]: updated };
    });
  };

  const handleUpdateArrayItem = (fieldName, idx, value) => {
    setEditData(prev => {
      const updated = [...(prev[fieldName] || [])];
      updated[idx] = value;
      return { ...prev, [fieldName]: updated };
    });
  };

  const handleAddImportantPointItem = (fieldName) => {
    setEditData(prev => ({
      ...prev,
      ImportantPoints: {
        ...prev.ImportantPoints,
        [fieldName]: [...(prev.ImportantPoints[fieldName] || []), '']
      }
    }));
  };

  const handleRemoveImportantPointItem = (fieldName, idx) => {
    setEditData(prev => {
      const updated = [...(prev.ImportantPoints[fieldName] || [])];
      updated.splice(idx, 1);
      return {
        ...prev,
        ImportantPoints: {
          ...prev.ImportantPoints,
          [fieldName]: updated
        }
      };
    });
  };

  const handleUpdateImportantPointItem = (fieldName, idx, value) => {
    setEditData(prev => {
      const updated = [...(prev.ImportantPoints[fieldName] || [])];
      updated[idx] = value;
      return {
        ...prev,
        ImportantPoints: {
          ...prev.ImportantPoints,
          [fieldName]: updated
        }
      };
    });
  };

  const handleSubmit = async () => {
    if (!script) return;

    const scriptId = script._id || script.id;
    if (!scriptId) {
      alert('Error: Script ID not found. Please try again.');
      return;
    }

    setLoading(true);
    try {
      const jsonContent = convertToJSON();
      const scriptIdString = String(scriptId);
      await dispatch(updateScript(scriptIdString, jsonContent, script.status || 'completed', null));
      onClose();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (!showModal || !script) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 pb-0">
          <h3 className="text-xl font-bold text-gray-900">Edit Script</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <CrossIcon />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 pt-4">
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Title</label>
            <input
              type="text"
              value={editData.Title}
              onChange={(e) => setEditData(prev => ({ ...prev, Title: e.target.value }))}
              className="input-primary text-base w-full"
              placeholder="Enter script title..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Introduction</label>
            <textarea
              value={editData.Introduction}
              onChange={(e) => setEditData(prev => ({ ...prev, Introduction: e.target.value }))}
              rows={4}
              className="input-primary resize-none text-base w-full"
              placeholder="Enter introduction..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Intro Roll-In</label>
            <textarea
              value={editData.IntroRollIn}
              onChange={(e) => setEditData(prev => ({ ...prev, IntroRollIn: e.target.value }))}
              rows={4}
              className="input-primary resize-none text-base w-full"
              placeholder="Enter intro roll-in..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">What Is / Core Concept</label>
            <textarea
              value={editData.WhatIsCoreConcept}
              onChange={(e) => setEditData(prev => ({ ...prev, WhatIsCoreConcept: e.target.value }))}
              rows={4}
              className="input-primary resize-none text-base w-full"
              placeholder="Enter core concept explanation..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Key Elements</label>
            <textarea
              value={editData.KeyElements}
              onChange={(e) => setEditData(prev => ({ ...prev, KeyElements: e.target.value }))}
              rows={4}
              className="input-primary resize-none text-base w-full"
              placeholder="Enter key elements..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-900">Settings & Tools</label>
              <button type="button" onClick={() => handleAddArrayItem('SettingsTools')} className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium">+ Add</button>
            </div>
            <div className="space-y-2">
              {editData.SettingsTools.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-start">
                  <span className="text-xs text-gray-500 mt-2.5">•</span>
                  <textarea
                    value={item}
                    onChange={(e) => handleUpdateArrayItem('SettingsTools', idx, e.target.value)}
                    rows={2}
                    className="input-primary text-sm flex-1 resize-none"
                    placeholder="Enter setting/tool..."
                  />
                  <button type="button" onClick={() => handleRemoveArrayItem('SettingsTools', idx)} className="p-1 text-red-500 hover:bg-red-50 rounded mt-1"><CrossIcon /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Extra Tip</label>
            <textarea
              value={editData.ExtraTip}
              onChange={(e) => setEditData(prev => ({ ...prev, ExtraTip: e.target.value }))}
              rows={4}
              className="input-primary resize-none text-base w-full"
              placeholder="Enter extra tip..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-900">Focusing & Stabilizing</label>
              <button type="button" onClick={() => handleAddArrayItem('FocusingStabilizing')} className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium">+ Add</button>
            </div>
            <div className="space-y-2">
              {editData.FocusingStabilizing.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-start">
                  <span className="text-xs text-gray-500 mt-2.5">•</span>
                  <textarea
                    value={item}
                    onChange={(e) => handleUpdateArrayItem('FocusingStabilizing', idx, e.target.value)}
                    rows={2}
                    className="input-primary text-sm flex-1 resize-none"
                    placeholder="Enter focusing/stabilizing tip..."
                  />
                  <button type="button" onClick={() => handleRemoveArrayItem('FocusingStabilizing', idx)} className="p-1 text-red-500 hover:bg-red-50 rounded mt-1"><CrossIcon /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-900">Composition Enhancements</label>
              <button type="button" onClick={() => handleAddArrayItem('CompositionEnhancements')} className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium">+ Add</button>
            </div>
            <div className="space-y-2">
              {editData.CompositionEnhancements.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-start">
                  <span className="text-xs text-gray-500 mt-2.5">•</span>
                  <textarea
                    value={item}
                    onChange={(e) => handleUpdateArrayItem('CompositionEnhancements', idx, e.target.value)}
                    rows={2}
                    className="input-primary text-sm flex-1 resize-none"
                    placeholder="Enter composition tip..."
                  />
                  <button type="button" onClick={() => handleRemoveArrayItem('CompositionEnhancements', idx)} className="p-1 text-red-500 hover:bg-red-50 rounded mt-1"><CrossIcon /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-900">Common Mistakes</label>
              <button type="button" onClick={() => handleAddArrayItem('CommonMistakes')} className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium">+ Add</button>
            </div>
            <div className="space-y-2">
              {editData.CommonMistakes.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-start">
                  <span className="text-xs text-gray-500 mt-2.5">•</span>
                  <textarea
                    value={item}
                    onChange={(e) => handleUpdateArrayItem('CommonMistakes', idx, e.target.value)}
                    rows={2}
                    className="input-primary text-sm flex-1 resize-none"
                    placeholder="Enter common mistake..."
                  />
                  <button type="button" onClick={() => handleRemoveArrayItem('CommonMistakes', idx)} className="p-1 text-red-500 hover:bg-red-50 rounded mt-1"><CrossIcon /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Editing & Refinement</label>
            <textarea
              value={editData.EditingRefinement}
              onChange={(e) => setEditData(prev => ({ ...prev, EditingRefinement: e.target.value }))}
              rows={4}
              className="input-primary resize-none text-base w-full"
              placeholder="Enter editing refinement tips..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Conclusion</label>
            <textarea
              value={editData.Conclusion}
              onChange={(e) => setEditData(prev => ({ ...prev, Conclusion: e.target.value }))}
              rows={4}
              className="input-primary resize-none text-base w-full"
              placeholder="Enter conclusion..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Call To Action</label>
            <textarea
              value={editData.CallToAction}
              onChange={(e) => setEditData(prev => ({ ...prev, CallToAction: e.target.value }))}
              rows={4}
              className="input-primary resize-none text-base w-full"
              placeholder="Enter call to action..."
            />
          </div>

          {/* Important Points */}
          <div className="border-t-2 border-gray-200 pt-5">
            <label className="block text-sm font-semibold text-gray-900 mb-4">
              Important Points
            </label>

            {/* Visual Cues and Scene Ideas */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-gray-500">
                  Visual Cues and Scene Ideas
                </label>
                <button
                  type="button"
                  onClick={() => handleAddImportantPointItem('visualCuesAndSceneIdeas')}
                  className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {editData.ImportantPoints.visualCuesAndSceneIdeas.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <span className="text-xs text-gray-500">•</span>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleUpdateImportantPointItem('visualCuesAndSceneIdeas', idx, e.target.value)}
                      className="input-primary text-sm flex-1"
                      placeholder="Enter visual cue..."
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImportantPointItem('visualCuesAndSceneIdeas', idx)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <CrossIcon />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Step by Step Demonstrations */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-gray-500">
                  Step by Step Demonstrations
                </label>
                <button
                  type="button"
                  onClick={() => handleAddImportantPointItem('stepByStepDemonstrations')}
                  className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {editData.ImportantPoints.stepByStepDemonstrations.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <span className="text-xs text-gray-500 w-6">{idx + 1}.</span>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleUpdateImportantPointItem('stepByStepDemonstrations', idx, e.target.value)}
                      className="input-primary text-sm flex-1"
                      placeholder="Enter demonstration step..."
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImportantPointItem('stepByStepDemonstrations', idx)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <CrossIcon />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Storytelling and Relatable Examples */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-gray-500">
                  Storytelling and Relatable Examples
                </label>
                <button
                  type="button"
                  onClick={() => handleAddImportantPointItem('storytellingAndRelatableExamples')}
                  className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {editData.ImportantPoints.storytellingAndRelatableExamples.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <span className="text-xs text-gray-500">•</span>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleUpdateImportantPointItem('storytellingAndRelatableExamples', idx, e.target.value)}
                      className="input-primary text-sm flex-1"
                      placeholder="Enter example..."
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImportantPointItem('storytellingAndRelatableExamples', idx)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <CrossIcon />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Engagement and Pacing Enhancers */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-gray-500">
                  Engagement and Pacing Enhancers
                </label>
                <button
                  type="button"
                  onClick={() => handleAddImportantPointItem('engagementAndPacingEnhancers')}
                  className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {editData.ImportantPoints.engagementAndPacingEnhancers.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <span className="text-xs text-gray-500">•</span>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleUpdateImportantPointItem('engagementAndPacingEnhancers', idx, e.target.value)}
                      className="input-primary text-sm flex-1"
                      placeholder="Enter enhancer..."
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImportantPointItem('engagementAndPacingEnhancers', idx)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <CrossIcon />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-gray-500">
                  Additional Notes
                </label>
                <button
                  type="button"
                  onClick={() => handleAddImportantPointItem('additionalNotes')}
                  className="text-xs text-[#78350F] hover:text-[#5a2709] font-medium"
                >
                  + Add
                </button>
              </div>
              <div className="space-y-2">
                {editData.ImportantPoints.additionalNotes.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <span className="text-xs text-gray-500">•</span>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleUpdateImportantPointItem('additionalNotes', idx, e.target.value)}
                      className="input-primary text-sm flex-1"
                      placeholder="Enter note..."
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImportantPointItem('additionalNotes', idx)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <CrossIcon />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        </div>

        <div className="flex gap-3 justify-end p-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 btn-primary rounded-lg font-semibold transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditScriptModal;
